import { ExtendedObservation } from '@seen/core/models/observation'
import { VisitNote } from '@seen/core/models/visitNote'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import type { JSX } from 'react'
import { useMemo } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useSearch } from '#src/hooks/index.ts'

interface VisitNoteVitalsWidgetProps {
  visitNote: VisitNote
}

interface VitalDisplay {
  label: string
  value: string
  tooltip: string
}

interface LoincLookup<V> {
  [code: string]: V
}

const LOINC_LABELS: LoincLookup<string> = {
  '85354-9': 'BP',
  '8867-4': 'HR',
  '9279-1': 'RR',
  '20564-1': 'O2',
  '8310-5': 'Temp',
  '38208-5': 'Pain',
  '29463-7': 'Weight',
  '8302-2': 'Height',
  '39156-5': 'BMI',
  '8287-5': 'Head Circ',
}

const LOINC_SORT_ORDER: LoincLookup<number> = {
  '85354-9': 0,
  '8867-4': 1,
  '9279-1': 2,
  '20564-1': 3,
  '8310-5': 4,
  '38208-5': 5,
  '29463-7': 6,
  '8302-2': 7,
  '39156-5': 8,
  '8287-5': 9,
}

const LOINC_DISPLAY_NAMES: LoincLookup<string> = {
  '85354-9': 'Blood pressure',
  '8867-4': 'Heart rate',
  '9279-1': 'Respiratory rate',
  '20564-1': 'Oxygen saturation',
  '8310-5': 'Body temperature',
  '38208-5': 'Pain level',
  '29463-7': 'Body weight',
  '8302-2': 'Body height',
  '39156-5': 'Body mass index',
  '8287-5': 'Head circumference',
}

function getLoincCode(obs: ExtendedObservation): string | undefined {
  return obs.codeableConcept.coding?.[0]?.code
}

function formatVitalValue(obs: ExtendedObservation): string | null {
  // A code recorded as components (blood pressure) reads as its template
  // formats it.
  if (obs.template) {
    return obs.componentDisplay ?? null
  }

  // valueQuantity
  if (obs.valueQuantity !== undefined) {
    const unit = obs.valueUnit ?? ''
    const value = obs.valueQuantity
    if (unit === '%') {
      return `${value}%`
    }
    if (unit === '[degF]' || unit === 'degF' || unit === 'fahrenheit') {
      return `${value} \u00B0F`
    }
    if (unit === '[degC]' || unit === 'degC') {
      return `${value} \u00B0C`
    }
    if (unit === '[in_us]') {
      return `${value} in`
    }
    if (unit === '[lb_av]') {
      return `${value} lbs`
    }
    if (unit === 'kg/m2') {
      return `${value} kg/m\u00B2`
    }
    if (unit === '{breaths}/min') {
      return `${value}/min`
    }
    if (unit === '{beats}/min') {
      return `${value} bpm`
    }
    return unit ? `${value} ${unit}` : `${value}`
  }

  // valueString
  if (obs.valueString) {
    return obs.valueString
  }

  return null
}

export function VisitNoteVitalsWidget({
  visitNote,
}: VisitNoteVitalsWidgetProps): JSX.Element | null {
  const observationIdString = useMemo(() => {
    const entries = Object.values(visitNote.sections)
      .flat()
      .filter((s) => VisitNote.isObservationSection(s))
      .flatMap((s) => s.entry ?? [])
    return entries
      .filter((e) => isOfType(e, 'Observation'))
      .map((r) => resolveId(r))
      .filter(Boolean)
      .join(',')
  }, [visitNote])

  const observationIds = useMemo(
    () => (observationIdString ? observationIdString.split(',') : []),
    [observationIdString]
  )

  const { data: observations, isLoading } = useSearch(
    ExtendedObservation,
    observationIds.length > 0
      ? ExtendedObservation.queryByIds(observationIds)
      : undefined
  )

  const vitals = useMemo<VitalDisplay[]>(() => {
    if (!observations?.length) {
      return []
    }

    const result: VitalDisplay[] = []
    const seen = new Set<string>()

    const sorted = [...observations].sort((a, b) => {
      const codeA = getLoincCode(a) ?? ''
      const codeB = getLoincCode(b) ?? ''
      return (LOINC_SORT_ORDER[codeA] ?? 99) - (LOINC_SORT_ORDER[codeB] ?? 99)
    })

    for (const obs of sorted) {
      const code = getLoincCode(obs)
      if (!code || seen.has(code)) {
        continue
      }
      seen.add(code)

      const label = LOINC_LABELS[code]
      if (!label) {
        continue
      }

      const value = formatVitalValue(obs)
      if (!value) {
        continue
      }

      result.push({ label, value, tooltip: LOINC_DISPLAY_NAMES[code] ?? label })
    }
    return result
  }, [observations])

  if (observationIds.length === 0) {
    return null
  }

  if (!isLoading && vitals.length === 0) {
    return null
  }

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
        <h4 className="text-sm font-medium">Vitals</h4>
        {!isLoading && vitals.length > 0 && (
          <span className="text-xs text-muted-foreground">{vitals.length}</span>
        )}
      </div>
      {isLoading ? (
        <div className="px-3 py-4 text-sm text-muted-foreground">
          Loading...
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 px-3 py-2">
          {vitals.map((vital) => (
            <div
              key={vital.label}
              className="flex items-baseline gap-1 text-sm"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="shrink-0 cursor-default font-medium text-muted-foreground">
                    {vital.label}:
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {vital.tooltip}
                </TooltipContent>
              </Tooltip>
              <span>{vital.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
